import React from 'react';
import { action, computed, observable, makeObservable } from 'mobx';
import { DateTime } from 'src_common/utils/time/time';
import { Common } from 'src/AppState/Common';
import { PopupsStackState } from 'src/AppState/PopupsStackState';
import { ToasterState } from 'src/AppState/ToasterState';
import { FilterOptionType, FilterValueType } from 'src/components/RealityCheckForm/CustomDropdown';
import { SelfExclusionPeriodForm } from './SelfExclusionPeriodForm';
import { ReactivateSelfExclusionStore } from './ReactivateSelfExclusion.store';

export enum SelfExclusionEnum {
    NOT_SELF_EXCLUDED,
    SELF_EXCLUDED,
    SELF_EXCLUSION_EXPIRED,
    INDEFINITE,
    COOLING_OFF,
    COOLING_OFF_PASSED,
}

export const SELF_EXCLUSION_OPTIONS: Record<string, string> = {
    6: '6 months',
    12: '1 year',
    24: '2 years',
    36: '3 years',
    48: '4 years',
    60: '5 years',
};

export interface SelfExclusionOptionsType {
    selfExclusionExpiry: string | null;
    coolingOffPeriodHours: number | null;
    reactivationRequestedDate: string | null;
    selfExclusionDuration: number | null;
    coolingOffEnds: DateTime | null;
    expiryDate: DateTime | null;
}

export class ReactivateSelfExclusionState {
    @observable public selectedValue: string | null = null;

    private popupStack: PopupsStackState;
    public readonly store: ReactivateSelfExclusionStore;

    public constructor(
        private readonly customerId: number,
        private readonly common: Common,
        private readonly permissionSelfExclusion: boolean
    ) {
        this.popupStack = PopupsStackState.get(this.common);
        this.store = new ReactivateSelfExclusionStore(common, customerId);
        makeObservable(this);

        void this.store.fetchSelfExclusionStatus();
        void this.store.fetchOnMatchExclusions();
    }

    @computed public get selfExclusionType(): SelfExclusionEnum {
        const status = this.store.exclusionStatus;

        if (status === null || status === undefined) {
            return SelfExclusionEnum.NOT_SELF_EXCLUDED;
        }

        if (status.reactivationAfter === undefined) {
            if (DateTime.fromUTC(status.reactivationAllowedAfter)?.isBefore(DateTime.current().utc()) === true) {
                return SelfExclusionEnum.SELF_EXCLUSION_EXPIRED;
            }
            return SelfExclusionEnum.SELF_EXCLUDED;
        }

        if (DateTime.fromUTC(status.reactivationAfter)?.isBefore(DateTime.current().utc()) === true) {
            return SelfExclusionEnum.COOLING_OFF_PASSED;
        }

        return SelfExclusionEnum.COOLING_OFF;
    }

    @computed public get isLoading(): boolean {
        return this.store.exclusionStatus === undefined;
    }

    @computed public get selfExclusionOptions(): SelfExclusionOptionsType {
        const status = this.store.exclusionStatus;
        return {
            selfExclusionExpiry: status?.starts ?? null,
            coolingOffPeriodHours: null,
            reactivationRequestedDate: null,
            selfExclusionDuration: null,
            coolingOffEnds:
                status?.reactivationAfter === undefined ? null : (DateTime.fromUTC(status.reactivationAfter) ?? null),
            expiryDate:
                status?.reactivationAllowedAfter === undefined
                    ? null
                    : (DateTime.fromUTC(status.reactivationAllowedAfter) ?? null),
        };
    }

    @computed public get canSetExclusion(): boolean {
        return this.selfExclusionOptions.selfExclusionExpiry === null;
    }

    public selfExclusionOptionsArray = (): Array<FilterOptionType<string>> => {
        const out: Array<FilterOptionType<string>> = [];

        for (const [key, value] of Object.entries(SELF_EXCLUSION_OPTIONS)) {
            out.push({
                value: key,
                label: value,
            });
        }

        return out;
    };

    @action public onSelectPeriod = (changedValue: FilterValueType): void => {
        if (typeof changedValue === 'string') {
            this.selectedValue = changedValue;
        }
    };

    private formatErrorMessage(message: string): string {
        return message.replace(/\w+:customer:\d+/g, (match) => {
            const accountId = match.split(':').pop() ?? '';
            return accountId === String(this.customerId)
                ? `Account ID: ${accountId} (this account)`
                : `Account ID: ${accountId}`;
        });
    }

    private handleReactivationError(e: unknown, title: string, fallback: string): void {
        const raw = e instanceof Error ? e.message : fallback;
        ToasterState.get(this.common).setToast({
            variant: 'error',
            title,
            message: this.formatErrorMessage(raw),
            persistent: true,
        });
    }

    @action public onReactivationRequest = async (): Promise<void> => {
        try {
            await this.store.requestReactivation();
        } catch (e) {
            this.handleReactivationError(
                e,
                'Reactivation Request Failed',
                'Failed to request reactivation. Please try again.'
            );
        }
    };

    @action public onReactivate = async (): Promise<void> => {
        try {
            await this.store.reactivate();
        } catch (e) {
            this.handleReactivationError(e, 'Reactivation Failed', 'Failed to reactivate account. Please try again.');
        }
    };

    @action public handleSetExclusionPeriod = (): void => {
        if (this.permissionSelfExclusion) {
            ToasterState.get(this.common).setToast({
                variant: 'error',
                title: 'Error!',
                message: 'You do not have permission to set self exclusion.',
            });
            return;
        }

        this.selectedValue = null;

        this.popupStack.addPopup('set_self_exclusion', {
            template: 'generic',
            hasOverlay: true,
            title: 'Set Self Exclusion Period',
            message: <SelfExclusionPeriodForm state={this} />,
            confirm_label: 'Set exclusion',
            cancel_label: 'Cancel',
            isLoading: false,
            onConfirm: async () => await this.handleConfirmSetExclusion(),
            onCancel: () => this.popupStack.closePopupOnTop(),
        });
    };

    @action private setPopupLoading = (isLoading: boolean): void => {
        const popup = this.popupStack.topPopup;

        if (popup !== undefined) {
            popup.isLoading = isLoading;
        }
    };

    @computed public get onMatchRows(): Array<{
        accountId: number;
        universe: string;
        dateOfSelfExcluded: string | undefined;
        selfExclusionStatus: string;
        firstName: string;
        surname: string;
        accountStatus: string;
    }> {
        const data = this.store.onMatchData;
        if (data === null || data === undefined) {
            return [];
        }

        return data.map((item) => {
            const status = item.status;
            let selfExclusionStatus = 'Not Excluded';

            if (status !== undefined) {
                if (status.reactivationAfter !== undefined) {
                    selfExclusionStatus = 'Cooling Off';
                } else if (
                    DateTime.fromUTC(status.reactivationAllowedAfter)?.isBefore(DateTime.current().utc()) === true
                ) {
                    selfExclusionStatus = 'Expired';
                } else {
                    selfExclusionStatus = 'Self-Excluded';
                }
            }

            const accountData = this.store.onMatchAccountData.get(item.accountId);

            return {
                accountId: item.accountId,
                universe: item.universe,
                dateOfSelfExcluded: status?.starts,
                selfExclusionStatus,
                firstName: accountData?.firstName ?? '',
                surname: accountData?.surname ?? '',
                accountStatus: accountData?.status ?? '',
            };
        });
    }

    @computed public get isOnMatchLoading(): boolean {
        return this.store.onMatchData === undefined;
    }

    @action private handleConfirmSetExclusion = async (): Promise<void> => {
        if (this.selectedValue === null) {
            ToasterState.get(this.common).setToast({
                variant: 'error',
                title: 'Error!',
                message: 'Please select an exclusion period.',
            });
            return;
        }

        if (this.selfExclusionOptions.selfExclusionExpiry !== null) {
            ToasterState.get(this.common).setToast({
                variant: 'error',
                title: 'Error!',
                message: 'You cannot set self exclusion when it is already set.',
            });
            this.popupStack.closePopupOnTop();
            return;
        }

        this.setPopupLoading(true);

        try {
            const selectedValueNumber = parseInt(this.selectedValue);
            await this.store.setSelfExclusion(selectedValueNumber);

            ToasterState.get(this.common).setToast({
                variant: 'success',
                title: 'Success',
                message: 'Self exclusion has been set successfully.',
            });

            this.popupStack.closePopupOnTop();
        } catch (e) {
            console.error(e);
            ToasterState.get(this.common).setToast({
                variant: 'error',
                title: 'Error!',
                message: 'Failed to set self exclusion. Please try again.',
            });
        } finally {
            this.setPopupLoading(false);
        }
    };
}
